interface PillStore {
  id: number
  code: string
  name: string | null
  city: string | null
  label: string
}

interface ShopPillsProps {
  stores: PillStore[]
  /** Empty is valid (the user cleared); the General Overview defaults to all entitled. */
  selectedIds: number[]
  onToggle: (id: number) => void
  onSelectAll: () => void
  onClear: () => void
  /** Hide the built-in Select all/Clear when a group pool owns those actions. */
  showControls?: boolean
}

const PILL_BASE =
  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors'
const PILL_ACTIVE =
  'border-salsa-500 bg-salsa-500 text-white shadow-sm hover:bg-salsa-600 dark:border-salsa-500'
const PILL_IDLE =
  'border-slate-200 bg-white text-slate-600 hover:border-salsa-300 hover:text-salsa-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:border-salsa-500/40 dark:hover:text-salsa-200'

/**
 * The shop filter as **pills** (dictated 2026-10-17), with an explicit select-all/clear.
 *
 * The 4-Wall screen arrives with nothing selected so it cannot quietly show a
 * company-wide total nobody asked for; the General Overview arrives with every
 * entitled shop selected. Each pill is a real toggle (`aria-pressed`), which is
 * what makes the control usable from the keyboard and readable by a screen reader.
 */
export function ShopPills({
  stores,
  selectedIds,
  onToggle,
  onSelectAll,
  onClear,
  showControls = true,
}: ShopPillsProps) {
  const allSelected = stores.length > 0 && selectedIds.length === stores.length
  const noneSelected = selectedIds.length === 0

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
          Shops
        </span>
        <span className="text-[11px] text-slate-500 dark:text-slate-400">
          {noneSelected
            ? 'none selected'
            : allSelected
              ? `all ${stores.length} selected`
              : `${selectedIds.length} of ${stores.length} selected`}
        </span>

        {showControls && (
          <div className="ml-auto flex gap-1">
            <button
              type="button"
              onClick={onSelectAll}
              className="rounded-lg border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 transition-colors hover:border-salsa-300 hover:text-salsa-700 dark:border-white/10 dark:text-slate-300 dark:hover:border-salsa-500/40 dark:hover:text-salsa-200"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={onClear}
              disabled={noneSelected}
              className="rounded-lg border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 transition-colors hover:border-salsa-300 hover:text-salsa-700 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:text-slate-300 dark:hover:border-salsa-500/40 dark:hover:text-salsa-200"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      <div className="flex max-h-40 flex-wrap gap-1.5 overflow-auto pr-1">
        {stores.map((store) => {
          const active = selectedIds.includes(store.id)
          return (
            <button
              key={store.id}
              type="button"
              onClick={() => onToggle(store.id)}
              aria-pressed={active}
              className={`${PILL_BASE} ${active ? PILL_ACTIVE : PILL_IDLE}`}
              title={store.name ?? store.city ?? store.label}
            >
              <span
                className={`font-mono text-[10px] ${active ? 'text-white/70' : 'text-slate-400 dark:text-slate-500'}`}
              >
                #{store.code}
              </span>
              <span className="max-w-[9rem] truncate">{store.name ?? store.city ?? store.label}</span>
            </button>
          )
        })}

        {stores.length === 0 && (
          <span className="text-xs text-slate-500 dark:text-slate-400">
            No shops available — showing your own store.
          </span>
        )}
      </div>
    </div>
  )
}
